import { Link } from "@/lib/nav";

import Money from "@/components/money";
import ResponsiveList, { ListCard } from "@/components/responsive-list";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@buildpy/ui/table";
import { EMPTY_MARK, formatDate } from "@buildpy/core/lib/format";
import type { ChangeOrder } from "@buildpy/core/modules/change-order/types";
import ChangeOrderStatusBadge from "@/modules/change-order/ui/status-badge";
import { changeOrderPath, extendDaysLabel } from "@buildpy/core/modules/change-order/view";

// 촘촘한 목록 표 — 공통 Table 기본(h-12·p-4)을 줄이고 왼쪽 여백을 없앤다.
const HEAD = "h-auto py-1 pl-0 pr-2 text-xs font-normal";
const CELL = "py-1.5 pl-0 pr-2";

interface Props {
  changeOrders: readonly ChangeOrder[];
  // 없으면 제목이 링크가 아니다(읽기 전용 이력).
  linkable?: boolean;
  testId?: string;
}

// 변경공사 목록 — 차수 순. 날짜는 결정일, 없으면 만든 날.
const ChangeOrderList = ({ changeOrders, linkable = true, testId = "change-order-list" }: Props) => {
  if (changeOrders.length === 0) {
    return <p className="text-sm text-muted-foreground">변경공사가 없습니다.</p>;
  }
  const dateOf = (co: ChangeOrder) => formatDate(co.decided_at ?? co.voided_at ?? co.created_at);
  const afterOf = (co: ChangeOrder) =>
    co.status === "ACCEPTED" && co.after_total !== null ? <Money value={co.after_total} /> : EMPTY_MARK;
  // 폰 카드 — 차수·제목·상태 한 줄, 증감·변경 후·날짜는 아래 세 칸(#23). sr-only 「, 」 는 칸끼리 붙어 읽히지 않게 하는 낭독 구분자. 공기 표시는 nowrap — 「+1 / 일」 처럼 구절 중간에서 끊기지 않게.
  const cards = changeOrders.map((co) => (
    <ListCard key={co.id} testId={`${testId}-card-${co.id}`}>
      <div className="flex min-w-0 items-start gap-x-2">
        <span className="shrink-0 tabular-nums text-muted-foreground">{co.seq}차</span>
        <span className="sr-only">, </span>
        <span className="min-w-0 flex-1 break-words">
          {linkable ? (
            <Link className="font-medium after:absolute after:inset-0" href={changeOrderPath(co.contract_id, co.id)}>
              {co.title}
            </Link>
          ) : (
            <span className="font-medium">{co.title}</span>
          )}
          {co.extend_days ? (
            <>
              <span className="sr-only">, </span>
              <span className="ml-1 whitespace-nowrap text-xs text-muted-foreground">{extendDaysLabel(co.extend_days)}</span>
            </>
          ) : null}
        </span>
        <span className="sr-only">, </span>
        <ChangeOrderStatusBadge status={co.status} />
      </div>
      <span className="sr-only">, </span>
      <dl className="grid grid-cols-3 gap-x-2 text-xs tabular-nums">
        <div className="min-w-0">
          <dt className="text-muted-foreground">증감</dt>
          <dd className="truncate">
            <Money signed value={co.delta_total} />
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-muted-foreground">변경 후</dt>
          <dd className="truncate">{afterOf(co)}</dd>
        </div>
        <div className="min-w-0 text-right">
          <dt className="text-muted-foreground">날짜</dt>
          <dd className="whitespace-nowrap">{dateOf(co)}</dd>
        </div>
      </dl>
    </ListCard>
  ));
  const table = (
    <Table data-testid={testId}>
      <TableHeader>
        <TableRow>
          <TableHead className={HEAD}>차수</TableHead>
          <TableHead className={HEAD}>제목</TableHead>
          <TableHead className={HEAD}>상태</TableHead>
          <TableHead className={`${HEAD} text-right`}>증감</TableHead>
          <TableHead className={`${HEAD} text-right`}>변경 후</TableHead>
          <TableHead className={`${HEAD} pr-0 text-right`}>날짜</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {changeOrders.map((co) => (
          <TableRow key={co.id}>
            <TableCell className={`${CELL} whitespace-nowrap tabular-nums`}>{co.seq}차</TableCell>
            <TableCell className={CELL}>
              {linkable ? (
                <Link className="underline-offset-2 hover:underline" href={changeOrderPath(co.contract_id, co.id)}>
                  {co.title}
                </Link>
              ) : (
                co.title
              )}
              {co.extend_days ? (
                <>
                  <span className="sr-only">, </span>
                  <span className="ml-1 whitespace-nowrap text-xs text-muted-foreground">{extendDaysLabel(co.extend_days)}</span>
                </>
              ) : null}
            </TableCell>
            <TableCell className={CELL}>
              <ChangeOrderStatusBadge status={co.status} />
            </TableCell>
            <TableCell className={`${CELL} text-right tabular-nums whitespace-nowrap`}><Money signed value={co.delta_total} /></TableCell>
            <TableCell className={`${CELL} text-right tabular-nums whitespace-nowrap`}>
              {afterOf(co)}
            </TableCell>
            <TableCell className={`${CELL} pr-0 text-right tabular-nums whitespace-nowrap`}>
              {dateOf(co)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  return <ResponsiveList cards={cards} label="변경공사 목록" table={table} testId={`${testId}-cards`} />;
};

export default ChangeOrderList;
